import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #availabilityObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#sortSoldOutProductsLast();
    this.#observeProductGrid();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#availabilityObserver?.disconnect();
  }

  /** Keeps available products before sold-out products as infinite-scroll pages are appended. */
  #observeProductGrid() {
    const { grid } = this.refs;
    if (!grid) return;

    this.#availabilityObserver = new MutationObserver(() => this.#sortSoldOutProductsLast());
    this.#availabilityObserver.observe(grid, { childList: true });
  }

  #sortSoldOutProductsLast() {
    const { grid } = this.refs;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [data-product-available]'));
    let foundSoldOut = false;
    let needsSorting = false;

    for (const card of cards) {
      const isAvailable = card.getAttribute('data-product-available') === 'true';
      if (!isAvailable) foundSoldOut = true;
      if (isAvailable && foundSoldOut) {
        needsSorting = true;
        break;
      }
    }

    if (!needsSorting) return;

    this.#availabilityObserver?.disconnect();
    const availableCards = cards.filter((card) => card.getAttribute('data-product-available') === 'true');
    const soldOutCards = cards.filter((card) => card.getAttribute('data-product-available') !== 'true');
    grid.append(...availableCards, ...soldOutCards);
    this.#availabilityObserver?.observe(grid, { childList: true });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
